import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  NumberInput,
  Paper,
  Stack,
  Table,
  Text,
  UnstyledButton,
  type MantineTheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { AppTooltip } from '../common/AppTooltip';
import { PaginationBar } from '../common/PaginationBar';
import { financialsApi } from '../../api/financials';
import {
  ComponentProjectFinancials,
  ComponentProjectTcfMatchMonth,
  FinancialAsOfPeriod,
} from '../../types/financials';
import { formatTimestampToLocal } from '../../utils/dateUtils';

/** Ledger months begin May 2026 and extend through next calendar month (same as subawardee spend). */
const REPORTING_PERIOD_START = dayjs('2026-05-01').startOf('month');
const PAGE_SIZE = 12;

const formatCurrency = (n: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);

const currencyFormatter = (value: string) => {
  if (value === undefined || value === null || value === '') return '';
  const cleaned = value.replace(/\$\s?|(,*)/g, '');
  if (cleaned === '' || Number.isNaN(Number(cleaned))) return value;
  return `$ ${cleaned}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const currencyParser = (value: string) => value.replace(/\$\s?|(,*)/g, '');

function monthKey(year: number, month: number): string {
  return `${year}-${month}`;
}

function comparePeriodDesc(
  a: { year: number; month: number },
  b: { year: number; month: number }
): number {
  if (a.year !== b.year) return b.year - a.year;
  return b.month - a.month;
}

type TcfMonthStatus = 'reported' | 'not_reported' | 'upcoming';

type TcfMonthRow = {
  year: number;
  month: number;
  label: string;
  status: TcfMonthStatus;
  tcfMatchToDate: number | null;
  updatedAt: string | null;
  updatedBy: string | null;
};

type KnownTcfMonth = {
  year: number;
  month: number;
  tcfMatchToDate: number | null;
  updatedAt?: string;
  updatedBy?: string;
};

function seedKnownMonths(
  tcfMatchToDate: number | null,
  tcfMatchAsOf: FinancialAsOfPeriod | null
): Record<string, KnownTcfMonth> {
  if (!tcfMatchAsOf || tcfMatchToDate == null) return {};
  const key = monthKey(tcfMatchAsOf.year, tcfMatchAsOf.month);
  return {
    [key]: {
      year: tcfMatchAsOf.year,
      month: tcfMatchAsOf.month,
      tcfMatchToDate,
    },
  };
}

function buildTcfMonthRows(known: Record<string, KnownTcfMonth>): TcfMonthRow[] {
  const now = dayjs();
  const currentYear = now.year();
  const currentMonth = now.month() + 1;
  const start = REPORTING_PERIOD_START;
  const end = now.add(1, 'month').startOf('month');
  const rows: TcfMonthRow[] = [];

  if (end.isBefore(start, 'month')) return rows;

  let cursor = start;
  while (cursor.isBefore(end) || cursor.isSame(end, 'month')) {
    const year = cursor.year();
    const month = cursor.month() + 1;
    const key = monthKey(year, month);
    const entry = known[key];
    const isFuture =
      year > currentYear || (year === currentYear && month > currentMonth);

    let status: TcfMonthStatus;
    if (isFuture) {
      status = 'upcoming';
    } else if (entry?.tcfMatchToDate != null) {
      status = 'reported';
    } else {
      status = 'not_reported';
    }

    rows.push({
      year,
      month,
      label: cursor.format('MMMM YYYY'),
      status,
      tcfMatchToDate: entry?.tcfMatchToDate ?? null,
      updatedAt: entry?.updatedAt ?? null,
      updatedBy: entry?.updatedBy ?? null,
    });

    cursor = cursor.add(1, 'month');
  }

  return rows.sort(comparePeriodDesc);
}

function projectCardPaperSx(theme: MantineTheme) {
  return {
    border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
    boxShadow:
      theme.colorScheme === 'dark'
        ? '0 0 12px rgba(255, 255, 255, 0.1)'
        : theme.shadows.md,
    overflow: 'hidden' as const,
    '@media (max-width: 768px)': {
      margin: theme.spacing.sm,
    },
  };
}

export type TcfMatchSectionProps = {
  componentProjectId: string;
  projectName: string;
  financials: ComponentProjectFinancials;
  canEdit: boolean;
};

/**
 * TCF match monthly ledger — same interaction model as subawardee federal/match spend.
 */
export function TcfMatchSection({
  componentProjectId,
  projectName,
  financials,
  canEdit,
}: TcfMatchSectionProps) {
  const queryClient = useQueryClient();
  const [knownMonths, setKnownMonths] = useState<Record<string, KnownTcfMonth>>(() =>
    seedKnownMonths(financials.tcfMatchToDate, financials.tcfMatchAsOf)
  );
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draftAmount, setDraftAmount] = useState<number | ''>('');
  const [page, setPage] = useState(1);
  const blurTimeoutRef = useRef<number | null>(null);
  const editDraftRef = useRef({ editingKey: null as string | null, draftAmount: '' as number | '' });

  useEffect(() => {
    editDraftRef.current = { editingKey, draftAmount };
  }, [editingKey, draftAmount]);

  useEffect(() => {
    setKnownMonths((prev) => {
      const seeded = seedKnownMonths(
        financials.tcfMatchToDate,
        financials.tcfMatchAsOf
      );
      return { ...prev, ...seeded };
    });
  }, [financials.tcfMatchAsOf, financials.tcfMatchToDate]);

  const allMonthRows = useMemo(() => buildTcfMonthRows(knownMonths), [knownMonths]);
  const totalPages = Math.max(1, Math.ceil(allMonthRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = allMonthRows.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );
  const rangeStart = allMonthRows.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, allMonthRows.length);

  const closeEditor = () => {
    setEditingKey(null);
    setDraftAmount('');
  };

  const mutation = useMutation({
    mutationFn: ({
      year,
      month,
      amount,
    }: {
      year: number;
      month: number;
      amount: number | null;
    }) =>
      financialsApi.upsertComponentProjectTcfMatch(
        componentProjectId,
        year,
        month,
        amount
      ),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['component-project-financials'] });

      if ('deleted' in result && result.deleted) {
        const key = monthKey(result.year, result.month);
        setKnownMonths((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        notifications.show({
          color: 'green',
          title: 'TCF match cleared',
          message: `Cleared TCF match through ${dayjs(
            `${result.year}-${String(result.month).padStart(2, '0')}-01`
          ).format('MMMM YYYY')}.`,
        });
        return;
      }

      const saved = result as ComponentProjectTcfMatchMonth;
      const key = monthKey(saved.year, saved.month);
      setKnownMonths((prev) => ({
        ...prev,
        [key]: {
          year: saved.year,
          month: saved.month,
          tcfMatchToDate: saved.tcfMatchToDate,
          updatedAt: saved.updatedAt,
          updatedBy: saved.updatedBy,
        },
      }));
      notifications.show({
        color: 'green',
        title: 'TCF match updated',
        message: `Saved TCF match through ${dayjs(
          `${saved.year}-${String(saved.month).padStart(2, '0')}-01`
        ).format('MMMM YYYY')}.`,
      });
    },
    onError: () => {
      notifications.show({
        color: 'red',
        title: 'Update failed',
        message: 'Could not save TCF match. Please try again.',
      });
    },
  });

  const saveDraft = async (
    rowKey: string,
    amount: number | ''
  ): Promise<boolean> => {
    const [yearStr, monthStr] = rowKey.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const row = allMonthRows.find((r) => monthKey(r.year, r.month) === rowKey);
    const nextAmount = amount === '' ? null : amount;
    const prevAmount = row?.tcfMatchToDate ?? null;

    if (nextAmount === prevAmount) return true;

    try {
      await mutation.mutateAsync({ year, month, amount: nextAmount });
      return true;
    } catch {
      return false;
    }
  };

  const commitOrCancel = async () => {
    const draft = editDraftRef.current;
    if (!draft.editingKey) return;
    await saveDraft(draft.editingKey, draft.draftAmount);
    closeEditor();
  };

  const openEditor = (row: TcfMonthRow) => {
    if (!canEdit || row.status === 'upcoming' || mutation.isPending) return;

    const nextKey = monthKey(row.year, row.month);
    if (editingKey === nextKey) return;

    if (editingKey && editingKey !== nextKey) {
      void commitOrCancel();
    }

    setEditingKey(nextKey);
    setDraftAmount(row.tcfMatchToDate ?? '');
  };

  const handleEditorKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void commitOrCancel();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      closeEditor();
    }
  };

  const handleEditorBlur = (rowKey: string) => {
    blurTimeoutRef.current = window.setTimeout(() => {
      const active = document.activeElement;
      const rowEl = document.querySelector(`tr[data-row-key="${rowKey}"]`);
      if (rowEl && active instanceof Node && rowEl.contains(active)) {
        return;
      }
      void commitOrCancel();
    }, 0);
  };

  const handleEditorFocus = () => {
    if (blurTimeoutRef.current !== null) {
      window.clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  };

  const editableCellSx = (theme: MantineTheme, editable: boolean) => ({
    display: 'block',
    width: '100%',
    minHeight: 36,
    padding: '6px 8px',
    borderRadius: theme.radius.sm,
    textAlign: 'right' as const,
    color: 'inherit',
    cursor: editable ? 'text' : 'default',
    border: '1px solid transparent',
    boxSizing: 'border-box' as const,
    '&:hover': editable
      ? {
          backgroundColor:
            theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
          borderColor:
            theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[3],
        }
      : undefined,
    '&:focus': editable
      ? {
          outline: `2px solid ${theme.colors.blue[5]}`,
          outlineOffset: 1,
        }
      : undefined,
  });

  const amountInputStyles = {
    root: { width: '100%' },
    wrapper: { width: '100%' },
    input: {
      minHeight: 36,
      height: 36,
      paddingTop: 6,
      paddingBottom: 6,
      paddingLeft: 8,
      paddingRight: 8,
      textAlign: 'right' as const,
    },
  };

  return (
    <Paper shadow="md" radius="md" sx={(theme) => projectCardPaperSx(theme)}>
      <Box
        p="md"
        sx={(theme) => ({
          borderBottom: `1px solid ${
            theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2]
          }`,
        })}
      >
        <Text size="lg" weight={700}>
          TCF Match Ledger
        </Text>
        <Text size="sm" color="dimmed">
          Enter running TCF match totals through the last day of each month for {projectName}.
        </Text>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table
          horizontalSpacing="md"
          verticalSpacing="sm"
          fontSize="sm"
          sx={(theme) => ({
            tableLayout: 'fixed',
            width: '100%',
            minWidth: 520,
            'th, td': {
              overflow: 'hidden',
              verticalAlign: 'middle',
            },
            'th:nth-of-type(1), td:nth-of-type(1)': {
              width: '28%',
            },
            'th:nth-of-type(2), td:nth-of-type(2)': {
              width: '32%',
            },
            'td:nth-of-type(2)': {
              textAlign: 'right',
            },
            'th:nth-of-type(3), td:nth-of-type(3)': {
              width: '40%',
            },
            thead: {
              backgroundColor:
                theme.colorScheme === 'dark'
                  ? theme.colors.dark[6]
                  : theme.colors.gray[0],
            },
            'thead th': {
              color: theme.colors.gray[6],
              fontWeight: 600,
              fontSize: theme.fontSizes.xs,
              textTransform: 'uppercase',
              letterSpacing: '0.02em',
            },
            'tbody tr[data-editing="true"]': {
              backgroundColor:
                theme.colorScheme === 'dark'
                  ? theme.fn.rgba(theme.colors.blue[9], 0.2)
                  : theme.fn.rgba(theme.colors.blue[0], 0.9),
            },
          })}
        >
          <thead>
            <tr>
              <th>
                <AppTooltip
                  label="Month-end this cumulative TCF match total is current through."
                  inline
                >
                  <span style={{ cursor: 'help' }}>Month end</span>
                </AppTooltip>
              </th>
              <th>
                <AppTooltip
                  label="Cumulative TCF match spend through the end of this month."
                  inline
                >
                  <span style={{ cursor: 'help' }}>Total TCF Match</span>
                </AppTooltip>
              </th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={3}>
                  <Text size="sm" color="dimmed" align="center" py="xl">
                    No reporting periods available yet.
                  </Text>
                </td>
              </tr>
            ) : (
              pageRows.map((row) => {
                const rowKey = monthKey(row.year, row.month);
                const isUpcoming = row.status === 'upcoming';
                const isReported = row.status === 'reported';
                const isEditing = editingKey === rowKey;
                const cellEditable = canEdit && !isUpcoming;

                return (
                  <tr
                    key={rowKey}
                    data-row-key={rowKey}
                    data-editing={isEditing ? 'true' : undefined}
                    style={{ opacity: isUpcoming ? 0.65 : 1 }}
                    onFocusCapture={handleEditorFocus}
                    onBlurCapture={() => {
                      if (isEditing) handleEditorBlur(rowKey);
                    }}
                  >
                    <td>
                      <Text size="sm" weight={500}>
                        {row.label}
                      </Text>
                      {isEditing ? (
                        <Text size="xs" color="dimmed">
                          Enter to save, Esc to cancel.
                        </Text>
                      ) : null}
                    </td>
                    <td>
                      {isEditing ? (
                        <NumberInput
                          value={draftAmount}
                          onChange={setDraftAmount}
                          min={0}
                          precision={0}
                          hideControls
                          formatter={currencyFormatter}
                          parser={currencyParser}
                          size="sm"
                          disabled={mutation.isPending}
                          onKeyDown={handleEditorKeyDown}
                          aria-label={`TCF match to date for ${row.label}`}
                          styles={amountInputStyles}
                          autoFocus
                        />
                      ) : (
                        <UnstyledButton
                          onClick={() => openEditor(row)}
                          disabled={!cellEditable}
                          aria-label={
                            row.tcfMatchToDate !== null
                              ? `Edit TCF match for ${row.label}`
                              : `Enter TCF match for ${row.label}`
                          }
                          sx={(theme) => editableCellSx(theme, cellEditable)}
                        >
                          <Text
                            size="sm"
                            weight={600}
                            ta="right"
                            sx={{ fontVariantNumeric: 'tabular-nums' }}
                          >
                            {row.tcfMatchToDate !== null
                              ? formatCurrency(row.tcfMatchToDate)
                              : '—'}
                          </Text>
                        </UnstyledButton>
                      )}
                    </td>
                    <td>
                      {isReported && row.updatedAt ? (
                        <Stack spacing={0}>
                          <Text size="sm">
                            {formatTimestampToLocal(row.updatedAt, 'MMM D, YYYY h:mm A')}
                          </Text>
                          {row.updatedBy ? (
                            <Text size="xs" color="dimmed" truncate>
                              {row.updatedBy}
                            </Text>
                          ) : null}
                        </Stack>
                      ) : (
                        <Text size="sm" color="dimmed">
                          —
                        </Text>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </Box>

      {totalPages > 1 ? (
        <Box
          p="md"
          sx={(theme) => ({
            borderTop: `1px solid ${
              theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2]
            }`,
          })}
        >
          <PaginationBar
            page={safePage}
            totalPages={totalPages}
            totalItems={allMonthRows.length}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            onChange={(nextPage) => {
              closeEditor();
              setPage(nextPage);
            }}
            mt={0}
            pt={0}
          />
        </Box>
      ) : null}
    </Paper>
  );
}
